import type {
  Sale,
  SaleDraftInput,
  SaleLineCommissionInput,
  SalesCatalogItem,
  SalesCatalogPageEntry,
  SalesPackage,
  SaleStockOptions,
} from '../../lib/api.js';
import type { SaleStockNeed } from './sale-fulfillment.js';

export type CartKind = 'SERVICE' | 'PRODUCT' | 'PACKAGE';

/// Uma linha da Nova venda: um serviço, um produto ou um pacote, pelo preço que vale hoje. O
/// servidor recalcula o preço ao confirmar; o daqui é o que a pessoa vê enquanto monta a venda.
export interface CartLine {
  key: string;
  kind: CartKind;
  id: string;
  label: string;
  priceCents: number;
  quantity: number;
  /// Os itens do pacote, para a linha dizer o que ele leva.
  summary?: string | undefined;
  /// Produtos que saem do estoque na hora, por unidade da linha (ADR 0046).
  products: Array<{ referenceId: string; label: string; quantity: number }>;
  /// Pacote com item por saldo pede quem recebe o saldo.
  requiresBeneficiary: boolean;
  beneficiary?: { id: string; name: string } | undefined;
  /// Os serviços que o pacote entrega por saldo: um deles pode ser o do atendimento de hoje.
  balanceServiceIds: string[];
}

export type AmountMode = 'BRL' | 'PCT';

export function itemLine(item: SalesCatalogItem): Omit<CartLine, 'quantity'> {
  return {
    key: `${item.kind}:${item.id}`,
    kind: item.kind,
    id: item.id,
    label: item.displayName,
    priceCents: item.priceCents,
    products:
      item.kind === 'PRODUCT'
        ? [{ referenceId: item.id, label: item.displayName, quantity: 1 }]
        : [],
    requiresBeneficiary: false,
    balanceServiceIds: [],
  };
}

/// O pacote pelo preço que vale hoje na unidade: o da promoção, quando há.
export function packageLine(found: SalesPackage): Omit<CartLine, 'quantity'> {
  return {
    key: `PACKAGE:${found.id}`,
    kind: 'PACKAGE',
    id: found.id,
    label: found.displayName,
    priceCents: found.promotion?.priceCents ?? found.priceCents,
    summary: packageSummary(found),
    products: found.items
      .filter((item) => item.kind === 'PRODUCT' && item.fulfillment === 'IMMEDIATE')
      .map((item) => ({
        referenceId: item.referenceId,
        label: item.referenceDisplayName,
        quantity: item.quantity,
      })),
    requiresBeneficiary: found.items.some((item) => item.fulfillment === 'BALANCE'),
    balanceServiceIds: found.items
      .filter((item) => item.kind === 'SERVICE' && item.fulfillment === 'BALANCE')
      .map((item) => item.referenceId),
  };
}

export function entryLine(entry: SalesCatalogPageEntry): Omit<CartLine, 'quantity'> {
  return entry.kind === 'PACKAGE' ? packageLine(entry.package) : itemLine(entry);
}

export function packageSummary(found: SalesPackage): string {
  return found.items
    .map((item) => `${String(item.quantity)}× ${item.referenceDisplayName}`)
    .join(' + ');
}

export function grossOf(lines: readonly CartLine[]): number {
  return lines.reduce((sum, line) => sum + line.priceCents * line.quantity, 0);
}

/// A porcentagem em centésimos de ponto (1000 é 10 %) aplicada ao valor, ao centavo mais próximo —
/// a mesma conta do servidor.
export function percentOf(baseCents: number, basisPoints: number): number {
  return Math.round((baseCents * basisPoints) / 10_000);
}

/// "10", "12,5" ou "12,5 %" em centésimos de ponto, de 0 a 100 %.
export function parsePercent(text: string): number {
  const normalized = text.replace(/[^\d,.]/gu, '').replace(',', '.');
  const value = Number(normalized);
  if (!normalized || !Number.isFinite(value)) return 0;
  return Math.min(Math.max(Math.round(value * 100), 0), 10_000);
}

export function formatPercent(basisPoints: number): string {
  return `${(basisPoints / 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} %`;
}

/// A porcentagem que um valor representa da base, para trocar de R$ para % sem perder o digitado.
export function basisPointsOf(cents: number, baseCents: number): number {
  return baseCents > 0 ? Math.min(Math.round((cents * 10_000) / baseCents), 10_000) : 0;
}

/// O que sai do estoque, somado por apresentação: o mesmo produto avulso e dentro de um pacote sai
/// uma vez só, do mesmo local.
export function stockNeeds(lines: readonly CartLine[]): SaleStockNeed[] {
  const total = new Map<string, SaleStockNeed>();
  for (const line of lines)
    for (const product of line.products) {
      const current = total.get(product.referenceId);
      total.set(product.referenceId, {
        referenceId: product.referenceId,
        label: product.label,
        quantity: (current?.quantity ?? 0) + product.quantity * Math.max(line.quantity, 1),
      });
    }
  return [...total.values()];
}

export type StockItem = SaleStockOptions['items'][number];
export type StockLot = StockItem['lots'][number];

/// De quais lotes sai a quantidade: o que vence primeiro sai primeiro, completando com os
/// seguintes. Lote vencido fica de fora — a mesma regra do servidor (ADR 0059).
export function lotPlan(
  lots: readonly StockLot[],
  quantity: number,
): Array<{ lot: StockLot; quantity: number }> {
  let left = quantity;
  const plan: Array<{ lot: StockLot; quantity: number }> = [];
  for (const lot of lots) {
    if (left <= 0) break;
    const room = lot.expired ? 0 : (lot.packages ?? 0);
    if (room <= 0) continue;
    const take = Math.min(room, left);
    plan.push({ lot, quantity: take });
    left -= take;
  }
  return plan;
}

/// O que falta na saída de um produto, antes de ir ao servidor: saldo no local ou a divisão manual
/// entre os lotes.
export function stockProblem(
  need: SaleStockNeed,
  item: StockItem | undefined,
  manual: Record<string, number> | undefined,
  locationName: string,
): string | null {
  const available = item?.available ?? 0;
  if (manual) {
    const placed = Object.values(manual).reduce((sum, quantity) => sum + quantity, 0);
    const over = item?.lots.find((lot) => (manual[lot.id] ?? 0) > (lot.packages ?? 0));
    if (over) return `O lote ${over.code} tem só ${String(over.packages ?? 0)}.`;
    if (placed !== need.quantity)
      return `Distribua as ${String(need.quantity)} unidades de ${need.label} entre os lotes (${String(placed)} distribuídas).`;
    return null;
  }
  if (need.quantity <= available) return null;
  return available === 0
    ? `${need.label}: sem saldo em ${locationName}.`
    : `${need.label}: ${locationName} tem só ${String(available)}.`;
}

/// Os lotes que vão ao servidor: só a divisão escolhida à mão; sem ela, o servidor tira do que
/// vence primeiro.
export function chosenLotsOf(
  needs: readonly SaleStockNeed[],
  manual: Record<string, Record<string, number>>,
): Array<{ referenceId: string; lotId: string; quantity: number }> {
  return needs.flatMap((need) =>
    Object.entries(manual[need.referenceId] ?? {})
      .filter(([, quantity]) => quantity > 0)
      .map(([lotId, quantity]) => ({ referenceId: need.referenceId, lotId, quantity })),
  );
}

export function saleItemsOf(
  lines: readonly CartLine[],
  commissionOf: (line: CartLine) => SaleLineCommissionInput | undefined = () => undefined,
): SaleDraftInput['items'] {
  return lines.map((line) => {
    const commission = commissionOf(line);
    const chosen = commission ? { commission } : {};
    return line.kind === 'PACKAGE'
      ? {
          packageId: line.id,
          quantity: line.quantity,
          ...(line.beneficiary ? { beneficiaryClientId: line.beneficiary.id } : {}),
          ...chosen,
        }
      : { kind: line.kind, referenceId: line.id, quantity: line.quantity, ...chosen };
  });
}

/// As linhas que a venda do atendimento já tem, agrupadas como foram vendidas: o pacote numa linha
/// só, com a parte do total dele.
export function saleLines(
  sale: Sale,
): Array<{ key: string; label: string; cents: number; appointmentService: boolean }> {
  const lines = new Map<
    string,
    { key: string; label: string; cents: number; appointmentService: boolean }
  >();
  for (const item of sale.items) {
    if (item.cancellation) continue;
    const key = item.packageId ? `PACKAGE:${item.packageId}` : item.id;
    const current = lines.get(key);
    lines.set(key, {
      key,
      label: item.packageId ? item.lineDisplayName : item.referenceDisplayName,
      cents: (current?.cents ?? 0) + item.allocatedTotalCents,
      appointmentService: Boolean(item.appointmentService),
    });
  }
  return [...lines.values()];
}
